import { Text, View, StyleSheet } from "@react-pdf/renderer";
import { ToWords } from "to-words";
import { Quotation, Setting } from "../../../generated/graphql";
import { useMemo } from "react";

const QuotationPaymentInfo: React.FC<{
  quotation: Quotation;
  settings?: Setting[];
}> = ({ quotation, settings }) => {
  const formatter = new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  const toWords = new ToWords({
    localeCode: "en-IN",
    converterOptions: {
      currency: true,
      ignoreDecimal: false,
      ignoreZeroCurrency: false,
      doNotAddOnly: true,
      currencyOptions: {
        name: "Dollar",
        plural: "Dollars",
        symbol: "$",
        fractionalUnit: {
          name: "Cent",
          plural: "Cents",
          symbol: "",
        },
      },
    },
  });

  const attention = useMemo(() => {
    return settings?.find((elm) => elm.key === "attentions");
  }, [settings]);

  const attentions = attention?.value.split("\n");

  return (
    <View style={styles.container}>
      <View style={styles.subTotalContainer}>
        <View style={{ flex: 1, maxWidth: "40%" }}>
          <View style={[styles.subtotalItem]}>
            <Text style={[styles.subtotalItemText, { width: 66 }]}>
              Subtotal
            </Text>
            <Text style={[styles.subtotalItemText]}>:</Text>
            <Text style={styles.subtotalItemText}>
              $ {formatter.format(quotation?.subTotal)}
            </Text>
          </View>
          <View style={styles.subtotalItem}>
            <Text style={[styles.subtotalItemText, { width: 66 }]}>
              Eddition Costa
            </Text>
            <Text style={styles.subtotalItemText}>:</Text>
            <Text style={styles.subtotalItemText}>
              ${" "}
              {quotation?.additionalCost
                ? formatter.format(quotation?.additionalCost)
                : 0}
            </Text>
          </View>
        </View>
        <View style={[{ flex: 1 }, styles.inWordsContainer]}>
          <Text
            style={[
              styles.inWordsText,
              { fontWeight: 700, fontFamily: "Poppins", marginBottom: 2 },
            ]}
          >
            In words:
          </Text>
          <Text style={styles.inWordsText}>
            {quotation?.total ? toWords.convert(quotation?.total) : ""}
          </Text>
        </View>
      </View>

      <View style={styles.totalContainer}>
        <Text style={[styles.totalText, { width: 66 }]}>NTA</Text>
        <Text style={styles.totalText}>:</Text>
        <Text style={styles.totalText}>
          $ {formatter.format(quotation?.total)}
        </Text>
      </View>

      <View style={styles.attentionBox}>
        <Text style={styles.attentionText}>Attention</Text>
        <View style={styles.attentionContent}>
          {attentions?.map((text, index) => (
            <View key={index} style={styles.footerTextLine}>
              <View style={styles.footerBullet} />
              <Text style={styles.footerText}>{text}</Text>
            </View>
          ))}
        </View>
      </View>
    </View>
  );
};

// Create styles
const styles = StyleSheet.create({
  container: {
    marginBottom: 10,
    fontFamily: "Poppins",
  },
  totalContainer: {
    backgroundColor: "#192855",
    display: "flex",
    flexDirection: "row",
    paddingLeft: 8,
    paddingRight: 8,
    paddingTop: 5,
    paddingBottom: 5,
    gap: 10,
    marginBottom: 6,
  },
  totalText: {
    fontSize: 13,
    textTransform: "uppercase",
    color: "#c44342",
    fontWeight: 600,
  },
  inWordsContainer: {
    marginBottom: 10,
  },
  inWordsText: {
    fontSize: 8,
  },
  subTotalContainer: {
    display: "flex",
    flexDirection: "row",
  },
  subtotalItem: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    gap: 10,
    paddingTop: 2,
    paddingBottom: 2,
    paddingLeft: 8,
    paddingRight: 8,
  },
  subtotalItemText: {
    fontSize: 8,
    fontWeight: 700,
    fontFamily: "Poppins",
  },
  attentionBox: {
    border: "1px solid #192855",
    padding: 5,
    display: "flex",
    flexDirection: "row",
  },
  attentionText: {
    color: "#c44342",
    textTransform: "uppercase",
    fontSize: 10,
    fontStyle: "italic",
    fontWeight: 700,
    fontFamily: "Poppins",
    width: 68,
  },
  attentionContent: {
    flex: 1,
    gap: 2,
  },
  footerTextLine: {
    display: "flex",
    flexDirection: "row",
    gap: 4,
  },
  footerBullet: {
    width: 3,
    height: 3,
    borderRadius: 10,
    marginTop: 3,
    backgroundColor: "#000",
  },
  footerText: {
    fontSize: 7,
    minWidth: 1,
    flexWrap: "nowrap",
    flex: 1,
  },
});

export default QuotationPaymentInfo;
